<script setup>
import { ref, computed } from 'vue';

const props = defineProps({
  data: {
    type: Array,
    required: true,
    default: () => [],
  },
});

const isOpen = ref(false);

const currentDayNumber = new Date().getDay();

const todaySchedule = computed(() => {
  return props.data.find((item) => item.days.includes(currentDayNumber));
});

const toggleDropdown = () => {
  isOpen.value = !isOpen.value;
};
</script>

<template>
  <div v-if="data" class="schedule" :class="{ 'schedule--open': isOpen }">
    <div class="schedule__current" @click="toggleDropdown">
      <CIcon
        class="schedule__current-icon"
        name="clock"
        width="16"
        height="16"
      />
      <div class="schedule__current-wrap">
        <div class="schedule__info">
          <span class="schedule__label">Сьогодні:</span>
          <span v-if="todaySchedule" class="schedule__hours">
            {{ todaySchedule.hours }}
          </span>
          <span v-else class="schedule__hours">Вихідний</span>
        </div>
        <CIcon
          class="schedule__icon"
          :class="{ 'schedule__icon--rotated': isOpen }"
          name="arrow-small-black"
          width="8"
          height="8"
        />
      </div>
    </div>

    <div v-if="isOpen" class="schedule__dropdown">
      <ul class="m-0 p-0 schedule__list">
        <li
          v-for="(item, index) in data"
          :key="index"
          class="schedule__item"
          :class="{
            'schedule__item--active': item.days.includes(currentDayNumber),
          }"
        >
          <span class="schedule__item-label">{{ item.label }}</span>
          <span class="schedule__item-hours">{{ item.hours }}</span>
        </li>
      </ul>
    </div>
  </div>
</template>

<style scoped lang="scss">
.schedule {
  @include link-small;
  position: relative;
  width: fit-content;
  font-family: inherit;
  cursor: pointer;
  user-select: none;
  display: none;
  @include media-breakpoint-up(md) {
    display: inline-block;
  }
  &__current {
    display: flex;
    align-items: center;
    gap: 8px;
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 8);
    }
    &-wrap {
      align-items: center;
      display: grid;
      grid-template-columns: auto 8px;
      gap: 4px;
      @include media-breakpoint-up(xl) {
        grid-template-columns: auto px-to-vw(8);
        @include responsive-spacing(gap, 4);
      }
    }
  }

  &__info {
    display: flex;
    gap: 4px;
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 4);
    }
  }

  &__icon {
    transition: transform 0.3s ease;
    .schedule--open & {
      transform: rotate(180deg);
    }
  }

  &__dropdown {
    background: $white;
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    width: 100%;
    min-width: 200px;
    border-radius: 8px;
    z-index: 10;
    overflow: hidden;
    @include media-breakpoint-up(xl) {
      top: calc(100% + px-tp-vw(4));
      @include responsive-spacing(min-width, 200);
      @include responsive-spacing(border-radius, 8);
    }
  }

  &__list {
    margin: 0;
    padding: 8px 0;
    list-style: none;
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(padding-top, 8);
      @include responsive-spacing(padding-bottom, 8);
    }
  }

  &__item {
    display: flex;
    justify-content: space-between;
    padding: 8px 16px;
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(padding-top, 8);
      @include responsive-spacing(padding-bottom, 8);
      @include responsive-spacing(padding-left, 16);
      @include responsive-spacing(padding-right, 16);
    }
    &--active {
      font-weight: 600;
      color: $primary;
    }

    &-label {
      color: $black;
      .schedule__item--active & {
        color: inherit;
      }
    }
  }
}
</style>
